<template>
  <b-row>
    <b-col cols="12">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="fw-bold mb-0">Manage BDM Profiles</h4>
        <b-button
          variant="primary"
          class="d-flex align-items-center"
          size="sm"
          @click="$bvModal.show('new-bdm-modal')"
        >
          <feather-icon icon="PlusIcon" /> Add BDM
        </b-button>
      </div>
    </b-col>

    <b-col cols="12" class="mt-2">
      <b-card>
        <div class="d-flex justify-content-between align-items-center">
          <span class="fw-semibold">BDMs</span>
          <b-form-input
            v-model="search"
            placeholder="Search BDMs..."
            size="sm"
            class="w-auto"
          ></b-form-input>
        </div>

        <b-table
          :key="tableKey"
          :items="items"
          :fields="fields"
          responsive
          :busy="loading"
          show-empty
          thead-class="text-nowrap"
          class="mt-1"
        >
          <template #cell(name)="{ item }">
            <span class="font-weight-bold">{{ item.username }}</span>
          </template>

          <template #cell(email)="data">
            <span>{{ data.item.email || "-" }}</span>
          </template>

          <template #cell(phone)="data">
            <span>{{ data.item.phoneNumber || "-" }}</span>
          </template>

          <template #cell(commission)="data">
            <span>{{ data.item.commission || "-" }}</span>
          </template>

          <template #cell(rate)="data">
            <span class="font-weight-bold">{{ data.item.rate || "-" }}</span>
          </template>

          <!-- Status -->
          <template #cell(status)="data">
            <b-badge
              :variant="statusVariant(data.item.status)"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <!-- Actions -->
          <template #cell(actions)="data">
            <div class="d-flex justify-content-end align-items-center">
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mr-1"
                @click="handleOpenModal(data.item, 'new-bdm-modal')"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button size="sm" variant="outline-warning" class="mr-1">
                <input
                  type="checkbox"
                  :id="`switch-btn-${data.item.code}`"
                  @change="handleUpdateStatus(data.item)"
                  hidden
                  :checked="data.item.status === 'inactive'"
                />
                <label
                  :for="`switch-btn-${data.item.code}`"
                  class="switch"
                ></label>
              </b-button>

              <b-button
                size="sm"
                variant="outline-danger"
                @click="handleOpenModal(data.item, 'confirm-delete-bdm')"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row v-if="pagination.total > 0">
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-end"
          >
            <b-pagination
              v-model="pagination.page"
              :total-rows="pagination.total"
              :per-page="pagination.limit"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </b-card>
    </b-col>

    <b-modal
      id="new-bdm-modal"
      :title="selectedAffiliate ? 'Edit BDM' : 'New BDM'"
      size="lg"
      centered
      body-class="modal-body-scrollable"
      @hide="handleCloseModal('new-bdm-modal')"
    >
      <b-container fluid class="text-black">
        <validation-observer ref="validateAffiliateForm">
          <b-form>
            <b-row>
              <b-col md="6">
                <b-form-group label="Code">
                  <validation-provider
                    #default="{ errors }"
                    name="code"
                    rules="required|min:3"
                  >
                    <b-form-input v-model="form.code" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Name">
                  <validation-provider
                    #default="{ errors }"
                    name="name"
                    rules="required|min:3"
                  >
                    <b-form-input v-model="form.name" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Username">
                  <validation-provider
                    #default="{ errors }"
                    name="username"
                    rules="required|min:3"
                  >
                    <b-form-input v-model="form.username" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Email">
                  <validation-provider
                    #default="{ errors }"
                    name="email"
                    rules="required|email"
                  >
                    <b-form-input v-model="form.email" type="email" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Password">
                  <validation-provider
                    #default="{ errors }"
                    name="password"
                    :rules="selectedAffiliate ? '' : 'required|min:6  '"
                  >
                    <b-form-input v-model="form.password" type="password" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Phone Number">
                  <validation-provider
                    #default="{ errors }"
                    name="phone number"
                    rules="required|min:10"
                  >
                    <b-form-input v-model="form.phoneNumber" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Status">
                  <b-form-select v-model="form.status" :options="statuses" />
                </b-form-group>
              </b-col>
              <!-- Role is fixed as BDM, no need to show in form -->
              <!-- <b-col md="4">
                <b-form-group label="Role">
                  <validation-provider
                    #default="{ errors }"
                    name="role"
                    rules="required"
                  >
                    <b-form-select v-model="form.roleId" :options="roles" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col> -->
              <b-col md="6">
                <b-form-group label="Commission Cap">
                  <b-form-input
                    v-model="form.commissionCap"
                    type="number"
                    placeholder="Enter commission cap"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <h6 class="font-weight-bold">Bank</h6>
            <b-row>
              <b-col md="4">
                <b-form-group label="Account Name">
                  <b-form-input v-model="form.accountName" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="BSB">
                  <b-form-input v-model="form.bsb" placeholder="XXX-XXX" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Account Number">
                  <b-form-input v-model="form.accountNumber" />
                </b-form-group>
              </b-col>
            </b-row>

            <h6 class="font-weight-bold">Address</h6>
            <b-row>
              <b-col md="12">
                <b-form-group label="Full Address">
                  <validation-provider
                    #default="{ errors }"
                    name="full address"
                    rules="required"
                  >
                    <b-form-input v-model="form.fullAddress" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Country">
                  <validation-provider
                    #default="{ errors }"
                    name="country"
                    rules="required|min:1"
                  >
                    <b-form-input v-model="form.country" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Street Number">
                  <validation-provider
                    #default="{ errors }"
                    name="street number"
                    rules="required"
                  >
                    <b-form-input v-model="form.streetNumber" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Street Name">
                  <b-form-input v-model="form.streetName" />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Street Type">
                  <b-form-input v-model="form.streetType" />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group label="Suburb">
                  <b-form-input v-model="form.suburb" />
                </b-form-group>
              </b-col>

              <b-col md="4">
                <b-form-group label="State">
                  <b-form-input v-model="form.state" />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label="Post Code">
                  <validation-provider
                    #default="{ errors }"
                    name="post code"
                    rules="required"
                  >
                    <b-form-input v-model="form.postCode" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Unit Number">
                  <b-form-input v-model="form.unitNumber" />
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-container>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="handleCloseModal('new-bdm-modal')"
          :disabled="loadingActions"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          class="ml-2"
          @click="handleCreateOrUpdateAffiliate"
          :disabled="loadingActions"
          >Save</b-button
        >
      </template>
    </b-modal>

    <b-modal
      id="confirm-delete-bdm"
      title="Delete BDM"
      centered
      hide-footer
      size="sm"
    >
      <div class="text-center text-black">
        <p class="mb-1" v-if="selectedAffiliate">
          Set this BDM <strong>{{ selectedAffiliate.name }}</strong> to
          Inactive?
        </p>
        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            class="mr-1"
            @click="handleCloseModal('confirm-delete-bdm')"
            :disabled="loadingActions"
          >
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="loadingActions"
            @click="handleDeleteAffiliate"
          >
            Confirm
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTable,
  BButton,
  BFormInput,
  BBadge,
  BFormCheckbox,
  BFormGroup,
  BContainer,
  BFormSelect,
  BInputGroup,
  BForm,
  BPagination,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import { debounce } from "debounce";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTable,
    BButton,
    BFormInput,
    BBadge,
    BFormCheckbox,
    BFormGroup,
    BContainer,
    BFormSelect,
    BInputGroup,
    BForm,
    ValidationObserver,
    ValidationProvider,
    BPagination,
  },
  data() {
    const roles = [{ value: null, text: "Select Role" }];
    const statuses = [
      {
        value: "active",
        text: "Active",
      },
      {
        value: "inactive",
        text: "Inactive",
      },
    ];
    const form = {
      code: "",
      name: "",
      username: "",
      password: "",
      email: "",
      phoneNumber: "",
      fullAddress: "",
      country: "",
      streetNumber: "",
      streetName: "",
      streetType: "",
      suburb: "",
      state: "",
      postCode: "",
      unitNumber: "",
      bankAccount: {
        number: "",
        name: "",
        bsb: "",
      },
      roleId: roles[0].value,
      status: statuses[0].value,
      commissionCap: "",
    };
    return {
      tableKey: 0,
      search: "",
      pagination: {
        page: 1,
        total: 0,
        limit: 10,
      },
      fields: [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "status", label: "Status", class: "text-center" },
        { key: "commission", label: "Commission Type" },
        { key: "rate", label: "Rate", class: "text-center" },
        { key: "actions", label: "Actions", class: "text-right" },
      ],
      items: [],
      form,
      statuses,
      roles,
      selectedAffiliate: null,
      loading: false,
    };
  },
  computed: {
    ...mapState("bdm/affiliates/actions", {
      loadingActions: "loadingActions",
    }),
  },
  watch: {
    search() {
      this.handleSearchDebounced();
    },
    "pagination.page"() {
      this.updateTableKey();
    },
  },
  created() {
    this.handleFetchAffiliates();
    this.handleFetchRoles();
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  methods: {
    ...mapActions("bdm/affiliates", {
      getAffiliates: "list/getAffiliates",
      updateAffiliate: "actions/updateAffiliate",
      createAffiliate: "actions/createAffiliate",
      deleteAffiliate: "actions/deleteAffiliate",
    }),
    ...mapActions("user/admin", {
      getRoleList: "getRoleList",
    }),
    async handleFetchAffiliates() {
      const { pagination, search } = this;
      this.loading = true;
      try {
        const {data,pagination:p} = await this.getAffiliates({
          page: pagination.page,
          limit: pagination.limit,
          ...(search && { search }),
        });
        this.items = data;
        this.pagination = p;
      } catch (error) {
        console.error("Error fetching affiliates:", error);
      } finally {
        this.loading = false;
      }
    },
    async handleFetchRoles() {
      try {
        const { data } = await this.getRoleList();
        const mappingRoles = data?.map((role) => ({
          value: role.roleId,
          text: role.name,
        }));
        this.roles = [...this.roles, ...mappingRoles];
        const bdmRole = mappingRoles?.find(
          (r) => r.text?.toLowerCase() === "bdm"
        );
        if (bdmRole) this.form.roleId = bdmRole.value;
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    },
    sanitizePayload(data) {
      const sanitize = (val) => {
        if (val === null || val === undefined || val === "") return undefined;
        if (Array.isArray(val)) {
          return val.length > 0 ? val : undefined;
        }
        if (typeof val === "object") {
          const cleaned = Object.fromEntries(
            Object.entries(val)
              .map(([k, v]) => [k, sanitize(v)])
              .filter(([_, v]) => v !== undefined)
          );
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }
        return val;
      };
      return Object.fromEntries(
        Object.entries(data)
          .map(([k, v]) => [k, sanitize(v)])
          .filter(([_, v]) => v !== undefined)
      );
    },
    normalizeValue(val) {
      if (val === "" || val === undefined) return null;
      return val;
    },
    getChangedFields(newData, oldData = {}) {
      const changed = {};
      Object.keys(newData).forEach((key) => {
        const newVal = this.normalizeValue(newData[key]);
        const oldVal = this.normalizeValue(oldData[key]);
        if (
          typeof newVal === "object" &&
          newVal !== null &&
          !Array.isArray(newVal)
        ) {
          const nested = this.getChangedFields(newVal, oldVal || {});
          if (Object.keys(nested).length > 0) changed[key] = nested;
          return;
        }
        if (newVal !== oldVal) changed[key] = newVal;
      });
      return changed;
    },
    async handleCreateOrUpdateAffiliate() {
      try {
        const valid = await this.$refs.validateAffiliateForm.validate();
        if (!valid) return;
        const normalizedForm = {
          ...this.form,
          phoneNumber: this.formatPhoneNumberAustralia(this.form.phoneNumber),
        };
        let payload;
        let result;
        if (this.selectedAffiliate) {
          payload = this.getChangedFields(
            normalizedForm,
            this.selectedAffiliate
          );
          payload = this.sanitizePayload(payload);
          result = await this.updateAffiliate({
            ...payload,
            roleId: this.form.roleId,
            code: this.selectedAffiliate.code,
          });
        } else {
          payload = this.sanitizePayload(normalizedForm);
          result = await this.createAffiliate({ ...payload, roleId: this.form.roleId });
        }
        if (result.data) {
          this.showToastSuccess(
            "Success",
            `Affiliate ${
              this.selectedAffiliate ? "updated" : "created"
            } successfully`
          );
          this.handleCloseModal("new-bdm-modal");
          this.refreshTable();
        }
      } catch (error) {
        console.error(error);
        this.showToastError("Something went wrong");
      }
    },
    async handleDeleteAffiliate() {
      const { selectedAffiliate } = this;
      if (!selectedAffiliate) return;
      try {
        const result = await this.deleteAffiliate(selectedAffiliate.code);
        if (result) {
          this.handleCloseModal("confirm-delete-bdm");
          this.showToastSuccess("Success", "Affiliate set to Inactive.");
          this.refreshTable();
        }
      } catch (error) {
        console.error("Error updating affiliate:", error);
      }
    },
    async handleUpdateStatus(affiliate) {
      if (!affiliate) return;
      try {
        const newStatus =
          affiliate.status === "inactive" ? "active" : "inactive";
        const result = await this.updateAffiliate({
          code: affiliate.code,
          status: newStatus,
        });
        if (result) {
          this.showToastSuccess("Success", "Affiliate status updated.");
          this.refreshTable();
        }
      } catch (error) {
        console.error("Error updating affiliate status:", error);
      }
    },
    statusVariant(status) {
      switch (status) {
        case "active":
          return "success";
        case "inactive":
          return "secondary";
        case "on leave":
          return "warning";
        default:
          return "light";
      }
    },
    handleSearch() {
      this.updateTableKey();
      this.handleFetchAffiliates();
    },
    updateTableKey() {
      this.tableKey++;
    },
    handleOpenModal(affiliate, modalName) {
      this.selectedAffiliate = affiliate;
      this.form = { ...this.form, ...affiliate };
      this.$bvModal.show(modalName);
    },
    handleCloseModal(modalName) {
      if (this.selectedAffiliate) {
        this.resetForm();
        this.selectedAffiliate = null;
      }
      this.$bvModal.hide(modalName);
    },
    resetForm() {
      this.form = {
        code: "",
        name: "",
        username: "",
        password: "",
        email: "",
        phoneNumber: "",
        fullAddress: "",
        country: "",
        streetNumber: "",
        streetName: "",
        streetType: "",
        suburb: "",
        state: "",
        postCode: "",
        unitNumber: "",
        bankAccount: {
          number: "",
          name: "",
          bsb: "",
        },
        roleId: this.roles[0].value,
        status: this.statuses[0].value,
        commissionCap: "",
      };
    },
    async refreshTable() {
      await this.handleFetchAffiliates();
      this.updateTableKey();
    },
    formatName(item) {
      const { firstName, middleName, lastName } = item;
      const names = [firstName, middleName, lastName].filter(Boolean);
      return names.join(" ");
    },
    formatPhoneNumberAustralia(phone) {
      if (!phone) return;
      const trimmed = phone.toString().trim();
      const auWithPlus = /^\+61\d{9}$/;
      if (auWithPlus.test(trimmed)) return trimmed;
      const digits = trimmed.replace(/\D/g, "");
      const normalized = digits.startsWith("0")
        ? digits.slice(1)
        : digits.startsWith("61")
        ? digits.slice(2)
        : digits;
      if (normalized.length !== 9) return phone;
      return `+61${normalized}`;
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>
<style lang="scss" scoped>
.switch {
  width: 20px;
  height: 12px;
  margin-bottom: 0;
  position: relative;
  display: inline-block;
  border-radius: 10px;
  background-color: #e0e0e0;
  cursor: pointer;
  transition: background-color 0.3s;
  &::after {
    content: "";
    position: absolute;
    top: 0px;
    left: 0px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #fff;
    transition: transform 0.3s;
  }
}
input[type="checkbox"]:checked + .switch {
  background-color: #ffc107;
}
input[type="checkbox"]:checked + .switch::after {
  transform: translateX(10px);
}
</style>
<style >
.modal-body-scrollable {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
